import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import RankBadge from '../components/RankBadge';
import MapboxAddressAutocomplete from '../components/MapboxAddressAutocomplete';
import OnboardingFlow from '../components/OnboardingFlow';
import SearchResultCard from '../components/SearchResultCard';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
    Search, 
    MapPin, 
    Plus, 
    FileText, 
    Loader2, 
    ArrowRight,
    Clock,
    Activity,
    ShieldCheck,
    RefreshCw
} from 'lucide-react';
import { addressAPI, reviewAPI, activityAPI } from '../lib/api';
import { formatDate, formatDateShort } from '../lib/utils';
import { toast } from 'sonner';

const Dashboard = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [myReviews, setMyReviews] = useState([]);
    const [loadingReviews, setLoadingReviews] = useState(true);
    const [searchPerformed, setSearchPerformed] = useState(false);
    const [activityFeed, setActivityFeed] = useState([]);
    const [loadingActivity, setLoadingActivity] = useState(true);
    const [processingMapbox, setProcessingMapbox] = useState(false);
    
    // Onboarding state - check localStorage
    const [showOnboarding, setShowOnboarding] = useState(() => {
        const hasSeenOnboarding = localStorage.getItem('clientcheck_onboarding_complete');
        return !hasSeenOnboarding;
    });

    useEffect(() => {
        loadMyReviews();
        loadActivityFeed();
    }, []);
    
    const handleOnboardingComplete = () => {
        localStorage.setItem('clientcheck_onboarding_complete', 'true');
        setShowOnboarding(false);
    };
    
    const handleOnboardingSkip = () => {
        localStorage.setItem('clientcheck_onboarding_complete', 'true');
        setShowOnboarding(false);
    };
    
    /**
     * Handle Mapbox address selection - directly route to property or review
     * This is the NEW frictionless flow:
     * - If address exists → go to property page
     * - If address doesn't exist → go to review form
     */
    const handleMapboxSelect = async (addressData) => {
        setSearchQuery(addressData.formatted_address || addressData.street_address);
        setProcessingMapbox(true);
        
        try {
            // Call quick-add API which checks for duplicates
            const response = await addressAPI.quickAdd({
                street_address: addressData.street_address?.trim() || '',
                city: addressData.city?.trim() || '',
                state: addressData.state?.trim() || '',
                zip_code: addressData.zip_code?.trim() || '',
                formatted_address: addressData.formatted_address || null,
                latitude: addressData.latitude,
                longitude: addressData.longitude
            });
            
            // Check if outside beta region
            if (response.is_outside_beta_region) {
                toast.error(response.message || 'This address is outside the current beta region.');
                setProcessingMapbox(false);
                return;
            }
            
            if (response.is_duplicate) {
                // Address EXISTS → go to property page
                navigate(`/property/${response.existing_property_id}`);
            } else {
                // Address DOES NOT exist → go to review form
                toast.success('Address added! Let\'s add your review.');
                navigate(`/review/new/${response.property_id}`);
            }
        } catch (error) {
            console.error('Address lookup error:', error);
            toast.error(error.response?.data?.detail || 'Failed to process address');
        } finally {
            setProcessingMapbox(false);
        }
    };

    const loadMyReviews = async () => {
        try {
            const response = await reviewAPI.getMyReviews();
            setMyReviews(response.data);
        } catch (err) {
            console.error('Failed to load reviews:', err);
        } finally {
            setLoadingReviews(false);
        }
    };

    const loadActivityFeed = async () => {
        try {
            const response = await activityAPI.getRecent();
            setActivityFeed(response.data || []);
        } catch (err) {
            console.error('Failed to load activity:', err);
        } finally {
            setLoadingActivity(false);
        }
    };

    const refreshActivity = async () => {
        setLoadingActivity(true);
        await loadActivityFeed();
    };

    const getTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);
        
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return formatDateShort(dateString);
    };

    // Get color based on data availability (NOT risk)
    const getDataStatusColor = (reviewCount) => {
        if (reviewCount >= 3) return 'text-primary';
        return 'text-muted-foreground';
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!searchQuery.trim() || searchQuery.length < 2) {
            toast.error('Please enter at least 2 characters to search');
            return;
        }

        setSearching(true);
        setSearchPerformed(true);

        try {
            const response = await addressAPI.search(searchQuery);
            setSearchResults(response.data);
        } catch (err) {
            toast.error('Search failed. Please try again.');
        } finally {
            setSearching(false);
        }
    };

    // Get rank color based on rank name
    const getRankColor = (rankName) => {
        const rankColors = {
            'Broom Pusher': 'text-slate-400',
            'Rookie': 'text-yellow-500',
            'Journeyman': 'text-emerald-500',
            'Pro': 'text-blue-500',
            'Veteran': 'text-amber-600',
            'Foreman': 'text-purple-500',
            'Concrete Cowboy': 'text-orange-500',
            'Legend': 'text-yellow-400',
            'Hey, Big Time!': 'text-primary'
        };
        return rankColors[rankName] || 'text-foreground';
    };

    return (
        <Layout>
            {/* Onboarding Flow for first-time users */}
            {showOnboarding && (
                <OnboardingFlow 
                    onComplete={handleOnboardingComplete}
                    onSkip={handleOnboardingSkip}
                />
            )}
            
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
                {/* Welcome Section - Compact */}
                <div className="mb-4 sm:mb-6">
                    <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground">
                        Welcome, {user?.company_name}
                    </h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Search addresses to check contractor experiences or submit reviews
                    </p>
                </div>

                {/* Search Section - Enhanced */}
                <Card className="bg-card border-border mb-4 sm:mb-6" id="quick-add-section">
                    <CardContent className="p-4 sm:p-6">
                        <div className="flex items-center gap-2 mb-3">
                            <Search className="h-4 w-4 text-primary" />
                            <span className="font-medium text-sm text-foreground">Search Job Address</span>
                        </div>
                        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
                            <div className="flex-1">
                                <MapboxAddressAutocomplete
                                    value={searchQuery}
                                    onChange={(val) => {
                                        setSearchQuery(val);
                                        // Reset search results when typing
                                        if (searchPerformed) {
                                            setSearchPerformed(false);
                                            setSearchResults([]);
                                        }
                                    }}
                                    onSelect={handleMapboxSelect}
                                    className="h-12"
                                    placeholder="Enter address (e.g., 123 Main St, San Diego CA)"
                                    data-testid="address-search-input"
                                    disabled={processingMapbox}
                                />
                            </div>
                            <Button
                                type="submit"
                                className="h-12 px-8 active:scale-[0.98] transition-transform"
                                disabled={searching || processingMapbox}
                                data-testid="address-search-btn"
                            >
                                {searching || processingMapbox ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <>
                                        <Search className="h-4 w-4 mr-2" />
                                        Search
                                    </>
                                )}
                            </Button>
                        </form>

                        {/* Processing Mapbox Selection */}
                        {processingMapbox && (
                            <div className="mt-4 flex justify-center py-8">
                                <div className="flex flex-col items-center gap-3">
                                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                    <p className="text-sm text-muted-foreground">Looking up address...</p>
                                </div>
                            </div>
                        )}

                        {/* Search Results - only show when not processing Mapbox */}
                        {searchPerformed && !processingMapbox && (
                            <div className="mt-4">
                                {searching ? (
                                    <div className="flex justify-center py-8">
                                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                    </div>
                                ) : searchResults.length > 0 ? (
                                    <div className="space-y-3">
                                        <p className="text-xs text-muted-foreground mb-2">
                                            {searchResults.length} address{searchResults.length !== 1 ? 'es' : ''} found
                                        </p>
                                        {searchResults.map((address) => (
                                            <SearchResultCard 
                                                key={address.id} 
                                                address={address}
                                                onClick={() => navigate(`/property/${address.id}`)}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-8" data-testid="search-no-results">
                                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted/50 mb-4">
                                            <MapPin className="h-8 w-8 text-muted-foreground" />
                                        </div>
                                        <h3 className="font-semibold text-foreground mb-2">No results found</h3>
                                        <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                                            Try selecting an address from the dropdown suggestions above.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Dashboard Grid - Compact */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                    {/* Rank Card - With Visual Badge */}
                    <Card className="bg-card border-border sm:col-span-2">
                        <CardContent className="p-4">
                            <div className="flex items-start gap-4">
                                {/* Rank Badge Visual */}
                                <div className="flex-shrink-0">
                                    <RankBadge 
                                        rank={user?.rank_name || 'Broom Pusher'} 
                                        size="lg" 
                                        showGlow={true}
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs text-muted-foreground mb-1">Your Rank</p>
                                    <p className={`font-bold text-xl ${getRankColor(user?.rank_name)}`}>
                                        {user?.rank_name || 'Broom Pusher'}
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                        {user?.rank_description || 'Just getting started and learning the ropes.'}
                                    </p>
                                    
                                    {/* Progress to next rank */}
                                    <div className="mt-3 space-y-1.5">
                                        <div className="flex justify-between items-center text-xs">
                                            <span className="font-medium text-foreground">
                                                {user?.rank_points || 0} pts
                                            </span>
                                            {user?.next_rank_name && (
                                                <span className="text-muted-foreground">
                                                    {user?.points_to_next_rank || 0} until <span className="text-foreground font-medium">{user?.next_rank_name}</span>
                                                </span>
                                            )}
                                        </div>
                                        <Progress 
                                            value={user?.progress_percent || 0} 
                                            className="h-2"
                                        />
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Trade Card */}
                    <Card className="bg-card border-border">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-accent">
                                    <FileText className="h-5 w-5 text-muted-foreground" />
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Your Trade</p>
                                    <p className="font-bold text-base text-foreground truncate">
                                        {user?.contractor_trade || 'Not set'}
                                    </p>
                                </div>
                            </div>
                            <p className="mt-2 text-xs text-muted-foreground">
                                Member since {formatDate(user?.created_at)}
                            </p>
                        </CardContent>
                    </Card>
                </div>

                {/* Recent Reviews - Compact */}
                <Card className="bg-card border-border mt-4">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-primary" />
                                <span className="font-medium text-sm text-foreground">Your Recent Reviews</span>
                            </div>
                            {myReviews.length > 0 && (
                                <Link to="/reviews/history">
                                    <Button variant="ghost" size="sm" className="h-7 text-xs">
                                        View All
                                        <ArrowRight className="h-3 w-3 ml-1" />
                                    </Button>
                                </Link>
                            )}
                        </div>
                        {loadingReviews ? (
                            <div className="flex justify-center py-6">
                                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                            </div>
                        ) : myReviews.length > 0 ? (
                            <div className="space-y-2">
                                {myReviews.slice(0, 5).map((review) => (
                                    <div
                                        key={review.id}
                                        className="flex items-center justify-between p-3 bg-accent/40 rounded-lg border border-border"
                                        data-testid={`my-review-${review.id}`}
                                    >
                                        <div className="min-w-0">
                                            <p className="font-medium text-sm text-foreground truncate">
                                                {review.job_type}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                {formatDateShort(review.created_at)}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            {review.has_verified_evidence && (
                                                <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
                                            )}
                                            <span className={`text-xs px-2 py-0.5 rounded ${
                                                review.status === 'approved' ? 'bg-emerald-600/20 text-emerald-500' :
                                                review.status === 'pending' ? 'bg-amber-600/20 text-amber-500' :
                                                'bg-red-600/20 text-red-500'
                                            }`}>
                                                {review.status}
                                            </span>
                                            <Link to={`/property/${review.address_id}`}>
                                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                                    <ArrowRight className="h-4 w-4" />
                                                </Button>
                                            </Link>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-6">
                                <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                                <p className="text-sm text-muted-foreground mb-2">
                                    No reviews yet
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    Search an address above to submit your first review
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Recent Job Activity Feed - Visible without scrolling on mobile */}
                <Card className="bg-card border-border mt-4" data-testid="activity-feed-card">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <Activity className="h-4 w-4 text-primary" />
                                <span className="font-medium text-sm text-foreground">Live Contractor Activity</span>
                            </div>
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0"
                                onClick={refreshActivity}
                                disabled={loadingActivity}
                                data-testid="refresh-activity-btn"
                            >
                                <RefreshCw className={`h-4 w-4 ${loadingActivity ? 'animate-spin' : ''}`} />
                            </Button>
                        </div>
                        {loadingActivity ? (
                            <div className="flex justify-center py-6">
                                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                            </div>
                        ) : activityFeed.length > 0 ? (
                            <div className="space-y-1.5">
                                {activityFeed.slice(0, 10).map((item, index) => (
                                    <div
                                        key={item.id || index}
                                        className="py-2 px-3 bg-accent/30 rounded-lg border border-border/50"
                                        data-testid={`activity-item-${index}`}
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <p className="text-sm text-foreground flex-1 min-w-0">
                                                <span className="font-semibold text-primary">{item.trade}</span>
                                                <span className="text-muted-foreground"> {item.activity_type} </span>
                                                <span className="font-medium">{item.partial_address}</span>
                                            </p>
                                            <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
                                                {getTimeAgo(item.created_at)}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-6">
                                <Activity className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                                <p className="text-sm text-muted-foreground">
                                    No recent activity
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Be the first to log a job!
                                </p>
                            </div>
                        )}
                        <p className="text-xs text-muted-foreground text-center mt-3 pt-2 border-t border-border/50">
                            Privacy protected • No contractor identities shown
                        </p>
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
};

export default Dashboard;
